"use client";
import { ChoicePills } from "@/components/marketing";
import { Input } from "@/components/ui/input";
import type {
  MembershipBenefitOption,
  MembershipKind,
  MembershipWhoOption,
} from "../../data";
import { useMembershipBuilder } from "./hooks";

interface MembershipBuilderProps {
  ariaLabel: string;
  kindStep: string;
  kindGroupLabel: string;
  kinds: { key: MembershipKind; label: string }[];
  priceLabel: string;
  initialPrice: number;
  priceStep: number;
  benefitsStep: string;
  benefits: MembershipBenefitOption[];
  whoStep: string;
  whoGroupLabel: string;
  who: MembershipWhoOption[];
  card: {
    free: { kind: string; title: string; cost: string };
    paid: { kind: string; title: string; cost: string };
    emptyBenefits: string;
  };
  fine: string;
}

const panelClass =
  "grid content-start gap-3.5 rounded-[28px] bg-white p-[22px] shadow-card sm:p-[26px]";
const stepClass = "text-base font-bold";

// The preview's membership builder: pick free or paid (with a yearly price),
// tick benefits and choose who qualifies; the dark card on the right shows
// the membership that results.
const MembershipBuilder = ({
  ariaLabel,
  kindStep,
  kindGroupLabel,
  kinds,
  priceLabel,
  initialPrice,
  priceStep,
  benefitsStep,
  benefits,
  whoStep,
  whoGroupLabel,
  who,
  card,
  fine,
}: MembershipBuilderProps) => {
  const builder = useMembershipBuilder({ benefits, who, initialPrice, card });

  return (
    <figure
      role="group"
      aria-label={ariaLabel}
      className="grid items-start gap-[22px] lg:grid-cols-[minmax(0,1fr)_minmax(0,1.05fr)]"
    >
      <div className={panelClass}>
        <h3 className={stepClass}>{kindStep}</h3>
        <ChoicePills
          ariaLabel={kindGroupLabel}
          options={kinds}
          value={builder.kind}
          onChange={builder.setKind}
        />
        {builder.isPaid && (
          <label className="grid gap-2 text-[15px] font-semibold">
            <span>{priceLabel}</span>
            <Input
              type="number"
              min={0}
              step={priceStep}
              value={builder.price}
              onChange={(event) => builder.setPrice(event.target.value)}
              className="h-auto rounded-[14px] border-[1.5px] border-line px-3.5 py-3 text-[22px] font-bold text-ink focus-visible:border-primary focus-visible:outline-3 focus-visible:outline-offset-1 focus-visible:outline-primary md:text-[22px]"
            />
          </label>
        )}

        <h3 className={stepClass}>{benefitsStep}</h3>
        <div className="grid gap-1.5">
          {benefits.map((benefit) => (
            <label
              key={benefit.key}
              className="flex cursor-pointer items-center gap-2.5 rounded-[14px] border-[1.5px] border-line px-3 py-2.5 font-semibold transition-colors has-checked:border-primary has-checked:bg-tone-blush"
            >
              <input
                type="checkbox"
                value={benefit.key}
                checked={builder.selected.includes(benefit.key)}
                onChange={() => builder.toggleBenefit(benefit.key)}
                className="size-[18px] shrink-0 accent-primary"
              />
              <span>{benefit.label}</span>
            </label>
          ))}
        </div>

        <h3 className={stepClass}>{whoStep}</h3>
        <ChoicePills
          ariaLabel={whoGroupLabel}
          options={who}
          value={builder.who}
          onChange={builder.setWho}
        />
      </div>

      <div aria-live="polite" className={`${panelClass} justify-items-center text-center`}>
        <div className="grid w-full max-w-[420px] gap-2.5 rounded-[26px] bg-linear-145 from-ink to-primary p-[26px] text-left text-white shadow-lift">
          <span className="text-xs font-extrabold tracking-[0.08em] text-line-warm uppercase">
            {builder.cardKind}
          </span>
          <span className="text-[1.8rem] leading-tight font-bold">
            {builder.cardTitle}
          </span>
          <span className="font-bold text-line-warm">{builder.cost}</span>
          <ul className="grid list-disc gap-[5px] pl-5">
            {builder.cardBenefits.map((benefit) => (
              <li key={benefit}>{benefit}</li>
            ))}
          </ul>
          <span className="text-[13.5px] text-line-warm">{builder.whoText}</span>
        </div>
        <p className="text-sm text-ink-muted">{fine}</p>
      </div>
    </figure>
  );
};

export default MembershipBuilder;
